import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Spinner } from './spinner';
import { Package } from 'lucide-react';

interface Column<T> {
  key: keyof T | string;
  header: string;
  render?: (item: T) => React.ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  getItemId: (item: T) => string;
  actionsColumn?: boolean;
  className?: string;
  // Pagination props (optional - if provided, footer shows pagination)
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export function DataTable<T>({
  columns,
  data,
  loading = false,
  onEdit,
  onDelete,
  getItemId,
  actionsColumn = true,
  className,
}: DataTableProps<T>) {
  const renderCell = (item: T, column: Column<T>) => {
    if (column.render) {
      return column.render(item);
    }
    const value = item[column.key as keyof T];
    return value as React.ReactNode;
  };

  return (
    <div className={cn('rounded-xl border border-border/60 overflow-hidden bg-surface shadow-sm', className)}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-neutral-50 border-b border-border">
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={cn(
                    'px-6 py-4 text-left text-xs font-semibold text-text-muted uppercase tracking-wider',
                    column.className
                  )}
                >
                  {column.header}
                </th>
              ))}
              {actionsColumn && (onEdit || onDelete) ? (
                <th className="px-6 py-4 text-right text-xs font-semibold text-text-muted uppercase tracking-wider w-24">
                  Actions
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + (actionsColumn ? 1 : 0)}
                  className="px-6 py-16 text-center"
                >
                  <div className="flex items-center justify-center gap-3">
                    <Spinner size="lg" />
                    <span className="text-text-secondary">Loading data...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (actionsColumn ? 1 : 0)}
                  className="px-6 py-16 text-center"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="p-3 bg-neutral-100 rounded-full">
                      <Package className="h-8 w-8 text-text-muted" />
                    </div>
                    <p className="text-text-secondary font-medium">No data available</p>
                    <p className="text-sm text-text-muted">Try adjusting your search or filters</p>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr
                  key={getItemId(item)}
                  className="hover:bg-neutral-50 transition-colors duration-150 group"
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className={cn('px-6 py-4 text-sm text-text-primary', column.className)}
                    >
                      {renderCell(item, column)}
                    </td>
                  ))}
                  {actionsColumn && (onEdit || onDelete) ? (
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {onEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEdit(item)}
                            aria-label={`Edit ${String(columns[0]?.render?.(item) || 'item')}`}
                            className="text-primary-600 hover:text-primary-700 hover:bg-primary-50 h-8 px-2"
                          >
                            Edit
                          </Button>
                        )}
                        {onDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDelete(item)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                            aria-label={`Delete ${String(columns[0]?.render?.(item) || 'item')}`}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
